import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Class as ClassIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import {
  useCourseHook,
  useClassHook,
  useSyllabusHook,
  useAssignSyllabusHook,
  useUnassignSyllabusHook,
} from "../../../hooks/useAcademicHook";
import {
  TCourse,
  TClass,
  TSyllabus,
} from "../../../apis/academic/academic.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import { hasCoursePrerequisite } from "./academic.helpers";
import { Info as InfoIcon } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";

const SyllabusAssignmentView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: courses, isLoading: coursesLoading } = useCourseHook();
  const { data: classes, isLoading: classesLoading } = useClassHook();
  const { data: syllabi, isLoading: syllabiLoading } = useSyllabusHook();
  const assignSyllabusMutation = useAssignSyllabusHook();
  const unassignSyllabusMutation = useUnassignSyllabusHook();
  const hasCourses = hasCoursePrerequisite(courses);

  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentType, setAssignmentType] = useState<"course" | "class">(
    "course",
  );
  const { control, watch, reset, getValues } = useForm({
    defaultValues: { entityId: "", syllabusId: "", academicYear: "" },
  });
  const selectedEntityId = watch("entityId");
  const selectedSyllabusId = watch("syllabusId");
  const academicYear = watch("academicYear");

  const isLoading = coursesLoading || classesLoading || syllabiLoading;

  // Group syllabi by course/class
  const syllabiByCourse = useMemo(() => {
    if (!syllabi || !courses) return new Map<string, TSyllabus[]>();
    const map = new Map<string, TSyllabus[]>();
    courses.forEach((course) => {
      const courseSyllabi = syllabi.filter((s) => s.courseId === course.id);
      if (courseSyllabi.length > 0) {
        map.set(course.id, courseSyllabi);
      }
    });
    return map;
  }, [syllabi, courses]);

  const syllabiByClass = useMemo(() => {
    if (!syllabi || !classes) return new Map<string, TSyllabus[]>();
    const map = new Map<string, TSyllabus[]>();
    classes.forEach((classItem) => {
      const classSyllabi = syllabi.filter((s) => s.classId === classItem.id);
      if (classSyllabi.length > 0) {
        map.set(classItem.id, classSyllabi);
      }
    });
    return map;
  }, [syllabi, classes]);

  // Get unassigned syllabi
  const unassignedSyllabi = useMemo(() => {
    if (!syllabi) return [];
    return syllabi.filter((s) => !s.courseId && !s.classId);
  }, [syllabi]);

  const handleOpenAssignmentDialog = (
    type: "course" | "class",
    entityId?: string,
  ) => {
    setAssignmentType(type);
    reset({ entityId: entityId || "", syllabusId: "", academicYear: "" });
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    reset({ entityId: "", syllabusId: "", academicYear: "" });
  };

  const handleAssignSyllabus = async () => {
    const { entityId, syllabusId, academicYear: year } = getValues();
    if (!syllabusId || !entityId || !year) {
      logger.show_error(
        "Please fill in all required fields: select a course/class, choose a syllabus, and enter an academic year.",
      );
      return;
    }

    if (!/^\d{4}-\d{4}$/.test(year)) {
      logger.show_error(
        "Academic year must be in format YYYY-YYYY (e.g., 2024-2025). Please enter a valid academic year.",
      );
      return;
    }

    // Validate academic year range
    const [startYear, endYear] = year.split("-").map(Number);
    if (endYear !== startYear + 1) {
      logger.show_error(
        "Academic year must span exactly one year (e.g., 2024-2025). The end year should be one year after the start year.",
      );
      return;
    }

    try {
      await assignSyllabusMutation.mutateAsync({
        syllabusId,
        courseId: assignmentType === "course" ? entityId : undefined,
        classId: assignmentType === "class" ? entityId : undefined,
        academicYear: year,
      });
      logger.show_success("Syllabus assigned successfully!");
      handleCloseAssignmentDialog();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to assign syllabus. Please try again.",
      );
    }
  };

  const handleUnassignSyllabus = async (syllabus: TSyllabus) => {
    try {
      await unassignSyllabusMutation.mutateAsync(syllabus.id);
      logger.show_success("Syllabus unassigned successfully!");
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to unassign syllabus. Please try again.",
      );
    }
  };

  const availableSyllabi = useMemo(() => {
    if (!syllabi) return [];
    // Show syllabi that are:
    // 1. Unassigned (no courseId and no classId)
    // 2. Already assigned to the selected entity (for reassignment)
    // 3. Match the assignment type (course syllabi for course assignment, class syllabi for class assignment)
    return syllabi.filter((s) => {
      // If no entity selected, show all unassigned syllabi
      if (!selectedEntityId) {
        return !s.courseId && !s.classId;
      }

      // If entity is selected, show:
      // - Unassigned syllabi that match the type
      // - Syllabi already assigned to this entity (for reassignment)
      if (assignmentType === "course") {
        // For course assignment: show unassigned syllabi or syllabi already assigned to this course
        const isUnassigned = !s.courseId && !s.classId;
        const isAssignedToThisCourse = s.courseId === selectedEntityId;
        // Also allow course-type syllabi that are unassigned
        const matchesType = s.type === "course" || (!s.courseId && !s.classId);
        return (isUnassigned || isAssignedToThisCourse) && matchesType;
      } else {
        // For class assignment: show unassigned syllabi or syllabi already assigned to this class
        const isUnassigned = !s.courseId && !s.classId;
        const isAssignedToThisClass = s.classId === selectedEntityId;
        // Also allow class-type syllabi that are unassigned
        const matchesType = s.type === "class" || (!s.courseId && !s.classId);
        return (isUnassigned || isAssignedToThisClass) && matchesType;
      }
    });
  }, [syllabi, selectedEntityId, assignmentType]);

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus Assignment`}
        />
        <Box p={3} data-testid="syllabus-assignment-view">
          <MainTitle title="Syllabus Assignment" />
          <Alert severity="info" data-testid="syllabus-assignment-no-institute">
            Please select an institute from the dashboard to manage syllabus
            assignments.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus Assignment`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="syllabus-assignment-view"
        >
          <CircularProgress data-testid="syllabus-assignment-loading" />
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Syllabus Assignment`}
      />

      <Grid container spacing={3} data-testid="syllabus-assignment-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="syllabus-assignment-header"
          >
            <MainTitle title="Syllabus Assignment" />
          </Box>
        </Grid>

        {/* Course Assignments */}
        <Grid item xs={12}>
          <Card data-testid="course-assignments-card">
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" data-testid="course-assignments-title">
                  Course Assignments
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAssignmentDialog("course")}
                  size="small"
                  disabled={!hasCourses}
                  title={
                    !hasCourses
                      ? "No courses available. Create courses first to assign syllabi to courses."
                      : ""
                  }
                  data-testid="add-course-assignment-button"
                >
                  Assign Syllabus
                </Button>
              </Box>

              {!hasCourses ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="150px"
                  gap={2}
                  data-testid="course-assignments-empty-state"
                >
                  <InfoIcon sx={{ fontSize: 48, color: "info.main" }} />
                  <Alert
                    severity="info"
                    sx={{ maxWidth: 600, width: "100%" }}
                    data-testid="no-courses-message"
                  >
                    No courses available. Create courses first to assign syllabi
                    to courses.
                  </Alert>
                </Box>
              ) : syllabiByCourse.size === 0 ? (
                <Alert
                  severity="info"
                  data-testid="no-course-assignments-message"
                >
                  No syllabi assigned to courses yet.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  data-testid="course-assignments-table"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Course</TableCell>
                        <TableCell>Assigned Syllabi</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(courses || [])
                        .filter((course) => syllabiByCourse.has(course.id))
                        .map((course) => {
                          const courseSyllabi =
                            syllabiByCourse.get(course.id) || [];
                          const groupedByYear = courseSyllabi.reduce(
                            (acc, syllabus) => {
                              if (!acc[syllabus.academicYear]) {
                                acc[syllabus.academicYear] = [];
                              }
                              acc[syllabus.academicYear].push(syllabus);
                              return acc;
                            },
                            {} as Record<string, TSyllabus[]>,
                          );

                          return (
                            <TableRow
                              key={course.id}
                              data-testid={`course-assignment-row-${course.id}`}
                            >
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {course.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {course.startGrade && course.endGrade
                                      ? `${course.startGrade}–${course.endGrade}`
                                      : course.obepId}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  {Object.entries(groupedByYear).map(
                                    ([year, yearSyllabi]) => (
                                      <Box
                                        key={year}
                                        data-testid={`course-year-group-${course.id}-${year}`}
                                      >
                                        <Chip
                                          label={year}
                                          size="small"
                                          variant="outlined"
                                          sx={{ mb: 0.5 }}
                                          data-testid={`course-year-chip-${year}`}
                                        />
                                        <Box
                                          display="flex"
                                          flexWrap="wrap"
                                          gap={0.5}
                                          sx={{ mt: 0.5 }}
                                        >
                                          {yearSyllabi.map((syllabus) => (
                                            <Chip
                                              key={syllabus.id}
                                              label={syllabus.name}
                                              size="small"
                                              onDelete={() =>
                                                handleUnassignSyllabus(syllabus)
                                              }
                                              data-testid={`course-syllabus-chip-${syllabus.id}`}
                                            />
                                          ))}
                                        </Box>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    handleOpenAssignmentDialog(
                                      "course",
                                      course.id,
                                    )
                                  }
                                  data-testid={`assign-syllabus-to-course-${course.id}`}
                                >
                                  Assign
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Class Assignments */}
        <Grid item xs={12}>
          <Card data-testid="class-assignments-card">
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" data-testid="class-assignments-title">
                  Class/Standard Assignments
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAssignmentDialog("class")}
                  size="small"
                  data-testid="add-class-assignment-button"
                >
                  Assign Syllabus
                </Button>
              </Box>

              {!classes || classes.length === 0 ? (
                <Alert severity="info" data-testid="no-classes-message">
                  No classes available. Create classes first to assign syllabi.
                </Alert>
              ) : syllabiByClass.size === 0 ? (
                <Alert
                  severity="info"
                  data-testid="no-class-assignments-message"
                >
                  No syllabi assigned to classes yet.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  data-testid="class-assignments-table"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Class/Standard</TableCell>
                        <TableCell>Assigned Syllabi</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classes
                        .filter((classItem) => syllabiByClass.has(classItem.id))
                        .map((classItem) => {
                          const classSyllabi =
                            syllabiByClass.get(classItem.id) || [];
                          const groupedByYear = classSyllabi.reduce(
                            (acc, syllabus) => {
                              if (!acc[syllabus.academicYear]) {
                                acc[syllabus.academicYear] = [];
                              }
                              acc[syllabus.academicYear].push(syllabus);
                              return acc;
                            },
                            {} as Record<string, TSyllabus[]>,
                          );

                          return (
                            <TableRow
                              key={classItem.id}
                              data-testid={`class-assignment-row-${classItem.id}`}
                            >
                              <TableCell>
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {classItem.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Grade {classItem.grade}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  {Object.entries(groupedByYear).map(
                                    ([year, yearSyllabi]) => (
                                      <Box
                                        key={year}
                                        data-testid={`class-year-group-${classItem.id}-${year}`}
                                      >
                                        <Chip
                                          label={year}
                                          size="small"
                                          variant="outlined"
                                          sx={{ mb: 0.5 }}
                                          data-testid={`class-year-chip-${year}`}
                                        />
                                        <Box
                                          display="flex"
                                          flexWrap="wrap"
                                          gap={0.5}
                                          sx={{ mt: 0.5 }}
                                        >
                                          {yearSyllabi.map((syllabus) => (
                                            <Chip
                                              key={syllabus.id}
                                              label={syllabus.name}
                                              size="small"
                                              onDelete={() =>
                                                handleUnassignSyllabus(syllabus)
                                              }
                                              data-testid={`class-syllabus-chip-${syllabus.id}`}
                                            />
                                          ))}
                                        </Box>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() =>
                                    handleOpenAssignmentDialog(
                                      "class",
                                      classItem.id,
                                    )
                                  }
                                  data-testid={`assign-syllabus-to-class-${classItem.id}`}
                                >
                                  Assign
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Unassigned Syllabi */}
        {unassignedSyllabi.length > 0 && (
          <Grid item xs={12}>
            <Card data-testid="unassigned-syllabi-card">
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 2 }}
                  data-testid="unassigned-syllabi-title"
                >
                  Unassigned Syllabi
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {unassignedSyllabi.map((syllabus) => (
                    <Chip
                      key={syllabus.id}
                      label={syllabus.name}
                      variant="outlined"
                      data-testid={`unassigned-syllabus-${syllabus.id}`}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignmentDialog}
        maxWidth="sm"
        fullWidth
        data-testid="assign-syllabus-dialog"
      >
        <DialogTitle data-testid="assign-syllabus-dialog-title">
          Assign Syllabus
        </DialogTitle>
        <DialogContent data-testid="assign-syllabus-dialog-content">
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <ControlledSelectField
              control={control as any}
              name="entityId"
              id="assign-entity-select"
              label={
                assignmentType === "course" ? "Course" : "Class/Standard"
              }
              isGridItem={false}
              itemList={
                assignmentType === "course"
                  ? hasCourses && courses
                    ? courses.map((course) => ({
                      key: course.id,
                      value: course.name,
                    }))
                    : []
                  : (classes || []).map((classItem) => ({
                    key: classItem.id,
                    value: `${classItem.name} (grade ${classItem.grade})`,
                  }))
              }
            />

            <ControlledSelectField
              control={control as any}
              name="syllabusId"
              id="assign-syllabus-select"
              label="Syllabus"
              isGridItem={false}
              itemList={availableSyllabi.map((syllabus) => ({
                key: syllabus.id,
                value: `${syllabus.name}${syllabus.academicYear ? ` (${syllabus.academicYear})` : ""}`,
              }))}
            />

            <ControlledTextField
              control={control as any}
              name="academicYear"
              id="assign-academic-year-input"
              label="Academic Year"
              isGridItem={false}
              fullWidth
              placeholder="2024-2025"
              helperText="Format: YYYY-YYYY (e.g., 2024-2025)"
              inputProps={{ "data-testid": "assign-academic-year-input" }}
            />
          </Box>
        </DialogContent>
        <DialogActions data-testid="assign-syllabus-dialog-actions">
          <Button
            onClick={handleCloseAssignmentDialog}
            data-testid="cancel-assign-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignSyllabus}
            variant="contained"
            disabled={
              !selectedEntityId ||
              !selectedSyllabusId ||
              !academicYear ||
              assignSyllabusMutation.isPending ||
              (assignmentType === "course" && !hasCourses)
            }
            data-testid="confirm-assign-button"
          >
            {assignSyllabusMutation.isPending ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SyllabusAssignmentView;
